import { isReactive, ref, toValue, watch } from 'vue'
import { storeToRefs } from 'pinia'
import qs from 'query-string'
import { useUserStore } from '@provise/stores/user'
import { useLockStore } from '@provise/stores/lock'
import { useConfigsStore } from '@/stores/configs'
import config from '@/config'

/**
 * @typedef {Object} UseApiResult
 * @property {import('vue').Ref} model
 * @property {import('vue').Ref} error
 * @property {import('vue').Ref} pending
 * @property {Function} refresh
 *
 * @typedef {Object} UseApiConfig
 * @property {String|import('vue').Ref} url
 * @property {String} method
 * @property {any} data initial value for result model ref; no usable if model given
 * @property {import('vue').Ref} model model that will be set after response
 * @property {Function} transform function for transform request body
 * @property {Function} pick function for transform response body
 * @property {Object|import('vue').Ref} query object to transform to query string
 * @property {Array<import('vue').Ref>|import('vue').Ref} watch when ref(s) changed, request will fetch again
 * @property {Boolean} upload if true, request will be multipart/form-data & data used for body
 * @property {Boolean} immediate
 */

const BACKEND = config.VITE_API_URL
const httpStatusesNoBody = [204, 201, 502]
const mapUri2Entity = new Map([
  [
    /^catalog\/(.+)\/(\d+)$/i,
    (uri, re) => {
      const [_, m, i] = re.exec(uri) // eslint-disable-line no-unused-vars
      return `${m}/${i}`
    },
  ],
  [
    /^stock\/(.+)\/(\d+)$/i,
    (uri, re) => {
      const [_, m, i] = re.exec(uri) // eslint-disable-line no-unused-vars
      return `${m}_stock/${i}`
    },
  ],
  [
    /^coordinator\/(.+)\/(\d+)$/i,
    (uri, re) => {
      const [_, m, i] = re.exec(uri) // eslint-disable-line no-unused-vars
      return `${m}/${i}`
    },
  ],
])

function useUpload(url, files) {
  const _url = `${BACKEND}${url}`
  const fd = new FormData()
  let i = 0
  for (let f of files) {
    fd.append(`file_${i}`, f)
    i++
  }
  return fetch(_url, {
    credentials: 'include',
    cache: 'no-cache',
    method: 'post',
    body: fd,
  })
}

export const downloadLink = file => `${BACKEND}download/${file.url}`

/**
 * @param {UseApiConfig} cfg
 * @returns {UseApiResult}
 */
const useApi = cfg => {
  const initialCfg = { ...cfg }
  const model = initialCfg.model ? initialCfg.model : ref(initialCfg.data || null)
  const transform = initialCfg.transform ? initialCfg.transform : d => d
  const pick = initialCfg.pick ? initialCfg.pick : d => d
  const isUpload = initialCfg.upload ?? false
  const watchSources = initialCfg.watch ? initialCfg.watch : []
  const error = ref(null)
  const pending = ref(false)
  const immediate = initialCfg.immediate === true || false
  const { user, isAuthorized } = storeToRefs(useUserStore())

  const { lockKeys: lkk } = storeToRefs(useLockStore())

  if (initialCfg.data) {
    delete initialCfg.data
  }

  const refresh = async (_cfg = null) => {
    const { isApiOk } = storeToRefs(useConfigsStore())
    let cfg = { ...initialCfg }

    if (_cfg !== null) {
      Object.assign(cfg, _cfg)
    }

    if (cfg.before instanceof Function) {
      if (cfg.before() === false) {
        return
      }
    }

    const baseUrl = determineBaseUrl(cfg)
    const url = new URL(toValue(cfg.url), baseUrl)
    const lk = getLockKey(url)
    let requestBody = null
    let responseBody = null
    let response
    const headers = {
      Accept: 'application/json',
    }

    if (!isUpload) {
      headers['Content-Type'] = 'application/json'
    }

    if (lk) {
      headers['X-Api-Lock'] = lk
    }

    if (isAuthorized.value) {
      headers.Authorization = `Bearer ${user.value.access_token}`
    }

    if (cfg.headers) {
      Object.assign(headers, cfg.headers)
    }

    if (isUpload) {
      // FormData instance
      requestBody = cfg.data
      cfg.method = 'post'
    } else if (model.value !== null && ['post', 'put'].includes(String(cfg.method).toLowerCase())) {
      requestBody = toValue(model)
    } else if (cfg.data) {
      requestBody = cfg.data
    }

    if (!isUpload && requestBody) {
      requestBody = JSON.stringify(transform(requestBody))
    }

    if (cfg.query) {
      url.search = qs.stringify(toValue(cfg.query))

      if (cfg.query.value?.filter) {
        const params = new URLSearchParams(url.search)
        const queryParts = []
        params.forEach((value, key) => {
          const values = value.split('&&')
          values.forEach(val => {
            queryParts.push(`${key}=${encodeURIComponent(val)}`)
          })
        })
        url.search = `?${queryParts.join('&')}`
      }
    }

    const fconfig = {
      headers,
      method: cfg.method || 'get',
      cache: 'no-cache',
      body: requestBody,
    }

    if (cfg.credentials !== false) {
      fconfig.credentials = 'include'
    }

    pending.value = true

    try {
      response = await fetch(url, fconfig)

      if (!response.ok) {
        isApiOk.value = false

        if (response.status === 401 || response.status === 400) {
          throw new ApiError(response.status)
        }
      }

      if (!httpStatusesNoBody.includes(response.status)) {
        responseBody = await response.json()
      }

      isApiOk.value = true
    } finally {
      pending.value = false
    }

    if (!response.ok) {
      error.value = responseBody?.response?.data || responseBody?.message || response.statusText

      throw new ApiError(
        responseBody?.error,
        responseBody?.error_description ?? response.statusText,
        response.status
      )
    }

    if (httpStatusesNoBody.includes(response.status)) {
      return null
    }

    let m = pick(responseBody)

    if (isReactive(model)) {
      Object.assign(model, m)
    } else {
      model.value = m
    }
  }

  if (watchSources && watch.length) {
    watch(watchSources, refresh, { deep: true })
  }

  function determineBaseUrl(cfg) {
    const urlPath = toValue(cfg.url)
    const method = String(cfg.method).toLowerCase()
    const queryEntity = toValue(cfg.query)?.entity_name
    const modelEntity = toValue(model)?.entity_name

    const ssoPaths = [
      /^\/web\/(login|autologin)$/,
      /^(login|logout|current_user|sso_redirect|roles(?:\/\d+)?|users(?:\/\d+(?:\/history)?)?|users_list|user_services)$/,
    ]

    if (ssoPaths.some(pattern => pattern.test(urlPath))) {
      return config.VITE_SSO_URL
    }

    const catalogsPaths = [
      /^catalog\/(medicines(?:\/\d+(?:\/history)?)?)$/,
      /^catalog\/(medicine_trademarks(?:\/\d+(?:\/history)?)?)$/,
      /^catalog\/(medicine_trademarks(?:\/\d+(?:\/merge\/\d+)?)?)$/,
    ]
    if (catalogsPaths.some(pattern => pattern.test(urlPath))) {
      return config.VITE_CATALOGS_URL
    }

    const catalogEntities = ['medicines', 'medicine_trademarks']

    const configPaths = [/^config(?:\/\d+)?$/]
    if (configPaths.some(pattern => pattern.test(urlPath))) {
      if (['put', 'post', 'delete'].includes(method) && catalogEntities.includes(modelEntity)) {
        return config.VITE_CATALOGS_URL
      }
    }

    const lockPaths = [/^(lock|lock_check|lock_reset)$/]
    if (lockPaths.some(pattern => pattern.test(urlPath))) {
      if (catalogEntities.includes(queryEntity)) {
        return config.VITE_CATALOGS_URL
      }
    }

    return BACKEND
  }

  /**
   * @param {URL} url
   */
  function getLockKey(url) {
    const be = new URL(BACKEND)
    const uri = url.pathname.replace(be.pathname, '')
    let kval = lkk?.value[uri]

    if (kval !== undefined) {
      return kval
    }

    for (const [lre, ltr] of mapUri2Entity) {
      if (!lre.test(uri)) {
        continue
      }
      const entity = ltr(uri, lre)
      kval = lkk.value[entity]
      if (!kval) {
        continue
      }
      break
    }

    return kval
  }

  if (immediate) {
    refresh()
  }

  return { model, error, pending, refresh }
}

/**
 * default API request on /someurl/:id
 * @param {string} url
 * @param {ref} model
 * @param {Function} refresh executuion function returned by useApi
 * @returns {Function} function that takes `method` as http method; if none given model will be saved (post - create/ put - update)
 */
const useApiResource =
  (url, model, refresh) =>
  (method = null) => {
    let cfg = {
      url,
    }

    const body = toValue(model)

    if (body.id) {
      cfg.url += `/${body.id}`
    }
    //saving
    if (method === null) {
      cfg = {
        ...cfg,
        data: body,
        method: body.id ? 'put' : 'post',
      }
    } else if (method === 'delete') {
      cfg = {
        ...cfg,
        data: body,
        method: method,
      }
    } else {
      cfg.method = method
    }

    return refresh(cfg)
  }

class ApiError extends Error {
  constructor(code, message, status) {
    super(message)
    this.name = 'ApiError'
    // error code from backend
    this.code = code ?? 500
    // http status
    this.status = status
  }
}

export { useApi, useApiResource, useUpload, ApiError }
